import dayjs from "dayjs";
import { ApplicationRole, ManagedUserResponse } from "../../../../../api";
import {
  EMPTY_USERS_FILTERS,
  MULTI_ROLE_OPTIONS,
  PENDING_OPTIONS,
  YES_NO_OPTIONS,
  ageOptionLabel,
  type UsersFiltersState,
} from "./types";

const DATE_KEYS: (keyof UsersFiltersState)[] = [
  "joinedFrom",
  "joinedTo",
  "updatedFrom",
  "updatedTo",
];

export function countActiveFilters(filters: UsersFiltersState): number {
  let count = 0;
  if (filters.role.trim()) count += 1;
  if (filters.pending.trim()) count += 1;
  if (filters.hasPhone.trim()) count += 1;
  if (filters.emailDomain.trim()) count += 1;
  if (filters.multiRole.trim()) count += 1;
  if (filters.joinedAge.trim()) count += 1;
  if (filters.updatedAge.trim()) count += 1;
  if (filters.joinedFrom != null) count += 1;
  if (filters.joinedTo != null) count += 1;
  if (filters.updatedFrom != null) count += 1;
  if (filters.updatedTo != null) count += 1;
  return count;
}

export function hasActiveFilters(filters: UsersFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: UsersFiltersState,
  key: keyof UsersFiltersState
): UsersFiltersState {
  if (DATE_KEYS.includes(key)) {
    return { ...filters, [key]: null };
  }
  return { ...filters, [key]: "" };
}

export function resetFilters(): UsersFiltersState {
  return { ...EMPTY_USERS_FILTERS };
}

function isInDateRange(
  value: string | undefined | null,
  from: dayjs.Dayjs | null,
  to: dayjs.Dayjs | null
): boolean {
  if (!from && !to) return true;
  if (!value) return false;
  const date = dayjs(value);
  if (!date.isValid()) return false;
  if (from && date.isBefore(from.startOf("day"))) return false;
  if (to && date.isAfter(to.endOf("day"))) return false;
  return true;
}

function matchesAgePreset(
  value: string | undefined | null,
  encoded: string
): boolean {
  if (!encoded) return true;
  if (encoded === "none") {
    return !value || !dayjs(value).isValid();
  }
  if (!value) return false;
  const date = dayjs(value);
  if (!date.isValid()) return false;

  const now = dayjs();
  const ageDays = now.diff(date, "day", true);

  const [op, raw] = encoded.split(":");
  const days = Number(raw);
  if (!Number.isFinite(days)) return true;

  if (op === "lte") return ageDays <= days;
  if (op === "gt") return ageDays > days;
  return true;
}

function getEmailDomain(email?: string | null): string {
  if (!email) return "";
  const at = email.lastIndexOf("@");
  if (at < 0 || at === email.length - 1) return "";
  return email.slice(at + 1).toLowerCase();
}

export function extractEmailDomains(
  users: ManagedUserResponse[]
): string[] {
  const domains = new Set<string>();
  users.forEach((user) => {
    const domain = getEmailDomain(user.email);
    if (domain) domains.add(domain);
  });
  return Array.from(domains).sort((a, b) => a.localeCompare(b));
}

export function matchesUsersFilters(
  user: ManagedUserResponse,
  filters: UsersFiltersState
): boolean {
  if (
    filters.role &&
    !user.roles?.includes(filters.role as ApplicationRole)
  ) {
    return false;
  }

  if (filters.pending === "yes" && !user.isPending) {
    return false;
  }
  if (filters.pending === "no" && user.isPending) {
    return false;
  }

  const hasPhone = Boolean(user.phoneNumber?.trim());
  if (filters.hasPhone === "yes" && !hasPhone) {
    return false;
  }
  if (filters.hasPhone === "no" && hasPhone) {
    return false;
  }

  if (filters.emailDomain) {
    const domain = getEmailDomain(user.email);
    if (domain !== filters.emailDomain.toLowerCase()) {
      return false;
    }
  }

  const roleCount = user.roles?.length ?? 0;
  if (filters.multiRole === "yes" && roleCount < 2) {
    return false;
  }
  if (filters.multiRole === "no" && roleCount !== 1) {
    return false;
  }

  if (!matchesAgePreset(user.createdAt, filters.joinedAge)) {
    return false;
  }
  if (!matchesAgePreset(user.updatedAt, filters.updatedAge)) {
    return false;
  }

  if (!isInDateRange(user.createdAt, filters.joinedFrom, filters.joinedTo)) {
    return false;
  }

  if (!isInDateRange(user.updatedAt, filters.updatedFrom, filters.updatedTo)) {
    return false;
  }

  return true;
}

export function buildActiveChips(
  filters: UsersFiltersState,
  formatRole: (role: string) => string
): { key: keyof UsersFiltersState; label: string }[] {
  const chips: { key: keyof UsersFiltersState; label: string }[] = [];

  if (filters.role) {
    chips.push({
      key: "role",
      label: `Role: ${formatRole(filters.role)}`,
    });
  }
  if (filters.pending) {
    const pendingLabel =
      PENDING_OPTIONS.find((o) => o.value === filters.pending)?.label ??
      filters.pending;
    chips.push({
      key: "pending",
      label: `Pending: ${pendingLabel}`,
    });
  }
  if (filters.hasPhone) {
    const phoneLabel =
      YES_NO_OPTIONS.find((o) => o.value === filters.hasPhone)?.label ??
      filters.hasPhone;
    chips.push({
      key: "hasPhone",
      label: `Has phone: ${phoneLabel}`,
    });
  }
  if (filters.emailDomain) {
    chips.push({
      key: "emailDomain",
      label: `Email domain: ${filters.emailDomain}`,
    });
  }
  if (filters.multiRole) {
    const multiLabel =
      MULTI_ROLE_OPTIONS.find((o) => o.value === filters.multiRole)?.label ??
      filters.multiRole;
    chips.push({
      key: "multiRole",
      label: `Roles: ${multiLabel}`,
    });
  }
  if (filters.joinedAge) {
    chips.push({
      key: "joinedAge",
      label: `Joined: ${ageOptionLabel(filters.joinedAge)}`,
    });
  }
  if (filters.updatedAge) {
    chips.push({
      key: "updatedAge",
      label: `Last updated: ${ageOptionLabel(filters.updatedAge)}`,
    });
  }
  if (filters.joinedFrom) {
    chips.push({
      key: "joinedFrom",
      label: `Joined from: ${dayjs(filters.joinedFrom).format("MM/DD/YYYY")}`,
    });
  }
  if (filters.joinedTo) {
    chips.push({
      key: "joinedTo",
      label: `Joined to: ${dayjs(filters.joinedTo).format("MM/DD/YYYY")}`,
    });
  }
  if (filters.updatedFrom) {
    chips.push({
      key: "updatedFrom",
      label: `Last updated from: ${dayjs(filters.updatedFrom).format("MM/DD/YYYY")}`,
    });
  }
  if (filters.updatedTo) {
    chips.push({
      key: "updatedTo",
      label: `Last updated to: ${dayjs(filters.updatedTo).format("MM/DD/YYYY")}`,
    });
  }

  return chips;
}
